import React from 'react'
import { css } from '@emotion/core'
import PropTypes from 'prop-types'
import { Ellipsis, WhiteTheme, colors, useTheme } from '@fs/zion-ui'
import { VisuallyHidden } from '@reach/visually-hidden'
import { useTranslation } from 'react-i18next'
import { i18n } from '@fs/zion-locale'
import { validFlavors, flavors, FLAGS } from '../config'
import { getLanguageName } from './helpers/languageHelpers'
import { useFlagsContext } from '../providers'
import { formatDuration, getEnv } from './helpers'

const badgeCss = css`
  background: ${colors.gray02};
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: 2px 7px;
  &[key-line='left'] {
    padding-top: 2px;
    padding-inline-end: 5px;
    padding-bottom: 2px;
    padding-inline-start: 0px;
  }
  border-radius: 3.6px;
  text-transform: uppercase;
  height: 20px;
  font-weight: 700;
  font-size: 12px;

  background-color: var(--background-color);
  color: var(--color);
`

export const Badge = ({ label, flavor = flavors.default, keyline, 'aria-label': ariaLabel, ...props }) => {
  return (
    <div
      css={badgeCss}
      style={{ '--background-color': flavors[flavor].solid, '--color': flavors[flavor].textColor || colors.gray00 }}
      {...props}
      key-line={keyline}
    >
      <Ellipsis aria-hidden="true">{label}</Ellipsis>
      <VisuallyHidden>{ariaLabel || label?.props?.children || label}</VisuallyHidden>
    </div>
  )
}

Badge.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  flavor: PropTypes.oneOf(validFlavors),
}

export const DurationBadge = ({ duration, customCss }) => {
  const [t] = useTranslation()
  duration = formatDuration(duration)
  const [seconds = 0, minutes = 0, hours = 0] = duration?.split(':').reverse() || []
  const durationIsZero = Number(duration?.replace(/:/g, '')) === 0
  const theme = useTheme()

  if (durationIsZero) return null

  const prettyDuration = `${hours > 0 ? `${hours}:` : ''}`
    .concat(`${minutes > 0 ? minutes : '00'}:`)
    .concat(`${seconds > 0 ? seconds : '00'}`)
  return (
    <Badge
      flavor={theme === 'night' ? 'charcoal' : 'platinum'}
      label={
        <time dateTime={`PT${hours}H${minutes}M${seconds}S`}>
          <WhiteTheme>{prettyDuration}</WhiteTheme>
        </time>
      }
      data-testid="badge-duration"
      css={customCss}
      aria-label={t('badge.duration.label', 'Video duration is {duration}', { duration: prettyDuration })}
    />
  )
}

export const SeriesBadge = () => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="seafoam"
      label={t('card.tag.series', 'Series')}
      data-testid="badge-series"
      aria-label={t('badge.series', 'This session is part of a series')}
    />
  )
}

export const LanguageBadge = ({ contentLocale }) => {
  const [t] = useTranslation()
  const userLocale = i18n.language
  const contentIsInUsersLanguage = contentLocale?.split('-')[0] === userLocale
  if (!contentLocale || contentIsInUsersLanguage || contentLocale === 'und') return null

  const translatedName = getLanguageName(contentLocale.split('-')[0])

  return (
    <Badge
      flavor="charcoal"
      label={translatedName}
      data-testid={`badge-language-${contentLocale}`}
      aria-label={t('badge.language', 'This session language is {translatedName}', { translatedName })}
    />
  )
}

export const LiveBadge = () => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="lava"
      label={t('card.tag.live', 'Live')}
      data-testid="badge-live"
      aria-label={t('badge.live', 'This session is live')}
    />
  )
}
export const ReplayBadge = () => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="lava"
      label={t('card.tag.replay', 'Replay')}
      data-testid="badge-replay"
      aria-label={t('badge.replay', 'This session is a replay')}
    />
  )
}

export const DraftBadge = () => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="draft"
      label={t('card.tag.draft', 'Draft')}
      data-testid="badge-draft"
      aria-label={t('badge.draft', 'This session is in draft')}
    />
  )
}

export const SubmittedBadge = () => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="submitted"
      label={t('card.tag.submitted', 'Submitted')}
      data-testid="badge-submitted"
      aria-label={t('badge.submitted', 'This session is submitted')}
    />
  )
}

export const EditableBadge = () => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="draft"
      label={t('card.tag.editable', 'In Progress')}
      data-testid="badge-editable"
      aria-label={t('badge.progress', 'This session is in progress')}
    />
  )
}

export const AcceptedBadge = () => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="accepted"
      label={t('card.tag.accepted', 'Accepted')}
      data-testid="badge-accepted"
      aria-label={t('badge.accepted', 'This session has been accepted')}
    />
  )
}

export const PublishedBadge = () => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="pending"
      label={t('card.tag.published', 'Published')}
      data-testid="badge-published"
      aria-label={t('badge.published', 'This session is published')}
    />
  )
}

export const PendingBadge = () => {
  const [t] = useTranslation()
  return <Badge flavor="pending" label={t('card.tag.pending', 'Pending')} data-testid="badge-pending" />
}

export const ApprovedBadge = () => {
  const [t] = useTranslation()
  return <Badge flavor="approved" label={t('card.tag.approved', 'Approved')} data-testid="badge-approved" />
}

export const DeclinedBadge = () => {
  const [t] = useTranslation()
  return <Badge flavor="declined" label={t('card.tag.declined', 'Declined')} data-testid="badge-declined" />
}

export const RevokedBadge = () => {
  const [t] = useTranslation()
  return <Badge flavor="declined" label={t('card.tag.revoked', 'Revoked')} data-testid="badge-revoked" />
}

export const PromotionBadge = () => {
  const [t] = useTranslation()
  return <Badge flavor="alllevels" label={t('card.tag.promotion', 'Promotion')} data-testid="badge-promotion" />
}

export const PDFBadge = () => {
  const [t] = useTranslation()
  return <Badge flavor="alllevels" label={t('card.tag.pdf', 'PDF')} data-testid="badge-pdf" />
}

export const ComingSoonBadge = () => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="charcoal"
      label={<WhiteTheme>{t('badge.coming-soon.label', 'Coming Soon')}</WhiteTheme>}
      aria-label={t('badge.coming-soon.label', 'Coming Soon')}
      data-testid="coming-soon-badge"
    />
  )
}

export const OnlineBadge = () => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="online"
      label={t('upload-content.session-location.online.label', 'Online')}
      data-testid="badge-online"
      aria-label={t('badge.online', 'This session is online')}
    />
  )
}

export const InPersonBadge = () => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="gold"
      label={t('upload-content.session-location.in-person.label', 'In person')}
      data-testid="badge-inperson"
      aria-label={t('badge.in-person', 'This session is in person')}
    />
  )
}

export const InPersonOnlyBadge = () => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="gold"
      label={t('upload-content.session-location.in-person-only.label', 'This class is not available online')}
      data-testid="badge-inpersononly"
      aria-label={t('badge.in-person-only', 'This session is in person only')}
    />
  )
}

export const SessionDifficultyBadge = ({ sessionDifficulty }) => {
  const [t] = useTranslation()

  const difficulties = {
    Beginner: { color: 'beginner', label: t('card.session-difficulty.beginner.label', 'Beginner') },
    Intermediate: { color: 'intermediate', label: t('card.session-difficulty.intermediate.label', 'Intermediate') },
    'Advanced/Professional': {
      color: 'advanced',
      label: t('card.session-difficulty.advanced.label', 'Advanced/Professional'),
    },
    All: { color: 'alllevels', label: t('card.session-difficulty.all.label', 'All Levels') },
  }
  const difficultyLabel = difficulties[sessionDifficulty].label

  if (!difficulties[sessionDifficulty]) {
    return null
  }

  return (
    <Badge
      flavor={difficulties[sessionDifficulty].color}
      label={difficultyLabel}
      data-testid="badge-difficulty"
      aria-label={t('badge.difficulty', 'This session has a skill level of {difficultyLabel}', { difficultyLabel })}
    />
  )
}

export const EnvironmentBadge = () => {
  const { [FLAGS.PREVIEW]: previewFlag } = useFlagsContext()
  const environments = {
    integration: { flavor: 'bronze', label: 'Integration' },
    beta: { flavor: 'silver', label: 'Beta' },
    // www: // NOTE: we don't want to show this is prod
  }
  const env = getEnv()

  if (!environments[env] && !previewFlag.isOn) {
    return null
  }

  return (
    <>
      {previewFlag.isOn && <Badge flavor="gold" label="Conference Preview" data-testid="badge-preview" />}
      {environments[env] && (
        <Badge flavor={environments[env].flavor} label={environments[env].label} data-testid="badge-environment" />
      )}
    </>
  )
}
